/**
 * Keeps the admin's place and unsaved edits across a sign-in after the session
 * expired. Saved to this tab's sessionStorage just before sign-in, read once
 * after it. Never holds a token, a secret or a webhook test token. Restored
 * edits are shown unsaved; nothing is saved for the admin.
 */

const RESUME_KEY = 'mediabriz_wizard_resume';
export const RESUME_MAX_AGE_MS = 60 * 60 * 1000;

export type ResumePage = 'landing' | 'settings' | 'actions';

export interface ResumeSnapshot {
  version: 1;
  orgId: string;
  page: ResumePage;
  savedAt: number;
  drafts: Record<string, unknown>;
}

/** Field names never written to the snapshot, at any depth (compared lower case). */
const SECRET_FIELDS = new Set([
  'secret',
  'clientsecret',
  'client_secret',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'idtoken',
  'token',
  'testtoken',
  'webhooktesttoken',
  'password',
  'privatekey',
  'authorization',
  'apikey',
]);

/** Copy without secret fields. Keeps `hasSecret` and other flags. */
export function stripSecrets<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => stripSecrets(item)) as T;
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_FIELDS.has(key.toLowerCase())) continue;
    out[key] = stripSecrets(item);
  }
  return out as T;
}

let restoredRow = 0;

/**
 * Copy with every `rowKey` replaced. Row keys count up from 1 on each page
 * load, so a restored row would otherwise share a key with a row added later.
 */
export function freshRowKeys<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => freshRowKeys(item)) as T;
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (key === 'rowKey' && typeof item === 'string') {
      restoredRow += 1;
      out[key] = `restored-row-${restoredRow}`;
    } else {
      out[key] = freshRowKeys(item);
    }
  }
  return out as T;
}

const providers = new Map<string, () => unknown>();
let pending: Record<string, unknown> | null = null;
let pendingResume: { orgId: string; page: ResumePage } | null = null;

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/**
 * A mounted screen offers its unsaved edits. The provider returns null or
 * undefined when there is nothing unsaved. Returns the unregister function.
 */
export function registerResumeDraft(key: string, provider: () => unknown): () => void {
  providers.set(key, provider);
  return () => {
    if (providers.get(key) === provider) providers.delete(key);
  };
}

export function saveResumeSnapshot(page: ResumePage, orgId: string, now = Date.now()): void {
  const drafts: Record<string, unknown> = {};
  for (const [key, provider] of providers) {
    try {
      const draft = provider();
      if (draft !== null && draft !== undefined) drafts[key] = stripSecrets(draft);
    } catch (error) {
      console.warn('[AUTH] Could not keep unsaved edits for', key, error);
    }
  }
  const snapshot: ResumeSnapshot = { version: 1, orgId, page, savedAt: now, drafts };
  try {
    storage()?.setItem(RESUME_KEY, JSON.stringify(snapshot));
  } catch (error) {
    console.warn('[AUTH] Could not keep the page for after sign-in', error);
  }
}

function hasStoredSnapshot(): boolean {
  try {
    return Boolean(storage()?.getItem(RESUME_KEY));
  } catch {
    return false;
  }
}

function readSnapshot(now: number): ResumeSnapshot | null {
  try {
    const raw = storage()?.getItem(RESUME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ResumeSnapshot>;
    if (parsed.version !== 1 || typeof parsed.orgId !== 'string' || typeof parsed.savedAt !== 'number') return null;
    if (parsed.page !== 'landing' && parsed.page !== 'settings' && parsed.page !== 'actions') return null;
    if (now - parsed.savedAt > RESUME_MAX_AGE_MS || parsed.savedAt > now + 60_000) return null;
    const drafts = parsed.drafts && typeof parsed.drafts === 'object' ? parsed.drafts : {};
    return { version: 1, orgId: parsed.orgId, page: parsed.page, savedAt: parsed.savedAt, drafts };
  } catch {
    return null;
  }
}

/**
 * Reads and removes the snapshot after sign-in. Returns the page to reopen
 * when it belongs to this org; the drafts then wait for their screens.
 */
export function beginResume(orgId: string, now = Date.now()): ResumePage | null {
  // A second setup run in the same load (React StrictMode in dev) gets the same answer.
  if (pendingResume && !hasStoredSnapshot()) return pendingResume.orgId === orgId ? pendingResume.page : null;
  const snapshot = readSnapshot(now);
  discardResumeSnapshot();
  pending = null;
  pendingResume = null;
  if (!snapshot || snapshot.orgId !== orgId) return null;
  pending = snapshot.drafts;
  pendingResume = { orgId: snapshot.orgId, page: snapshot.page };
  return snapshot.page;
}

export function discardResumeSnapshot(): void {
  try {
    storage()?.removeItem(RESUME_KEY);
  } catch {
    // Nothing stored.
  }
}

/** The restored draft for a screen, or null. Stays until `endResume` so a remount sees it too. */
export function resumeDraft<T>(key: string): T | null {
  const draft = pending?.[key];
  return draft === undefined ? null : draft as T;
}

/** Drops restored drafts once the admin leaves the reopened page. */
export function endResume(): void {
  pending = null;
  pendingResume = null;
}
